'use client';

import type { ChangeEvent } from 'react';
import { useTranslations } from 'next-intl';
import { VStack } from '@terra/ui-styled-system';
import {
  Field,
  Input,
  ObjectEditField,
  ObjectEditFieldGroup,
  ObjectEditSection,
  Select,
  Textarea,
  type EditableFields,
} from '@terra/ui-design-system';
import type {
  FindingStatusType,
  SignalCategoryType,
} from '@terra/shared-schemas-orchestrator';
import { Tick01Icon } from '@terra/ui-icons';
import type {
  FindingEditAssetCollection,
  FindingEditMarkableFieldProps,
  FindingEditRunCollection,
  FindingEditSelectCollection,
  FindingEditSendFieldContext,
  FindingEditSignalCollection,
} from './findingEditForm.types';
import { FindingEditAssetsMultiSelect } from './FindingEditAssetsMultiSelect';
import { FindingEditSignalCombobox } from './FindingEditSignalCombobox';
import { FindingEditRunCombobox } from './FindingEditRunCombobox';
import { FindingLinkedTicketActions } from './FindingLinkedTicketActions';
import { ObjectContextMarkableField } from '../object-context/ObjectContextMarkableField';
import type { FindingEditableField } from '../../store/findingEditStore';

type FindingEditGeneralDetailsSectionProps = {
  editableFields: EditableFields;
  statusOptions: FindingEditSelectCollection;
  categoryOptions: FindingEditSelectCollection;
  signalsCollection: FindingEditSignalCollection;
  runsCollection: FindingEditRunCollection;
  assetsCollection: FindingEditAssetCollection;
  isSearchingSignals: boolean;
  isSearchingRuns: boolean;
  isSearchingAssets: boolean;
  currentRunValue: string | null;
  linkedTicketUrl: string | null;
  canEditLinkedTicket: boolean;
  hasLinkedTicket: boolean;
  sendSelectionToChatLabel: string;
  handleFieldChange: <T extends keyof EditableFields>(
    field: T,
    value: EditableFields[T],
  ) => void;
  handleStatusFieldChange: (status: FindingStatusType) => void;
  handleAssetsMultiChange: (selectedIds: string[]) => void;
  handleSignalSearch: (query: string) => void;
  handleRunSearch: (query: string) => void;
  handleAssetSearch: (query: string) => void;
  handleRunChange: (traceThreadId: string | null) => void;
  handleSendFieldContextToChat: (context: FindingEditSendFieldContext) => void;
  handleOpenLinkedTicketDialog: () => void;
  getMarkableFieldProps: (
    field: FindingEditableField,
  ) => FindingEditMarkableFieldProps;
};

export function FindingEditGeneralDetailsSection({
  editableFields,
  statusOptions,
  categoryOptions,
  signalsCollection,
  runsCollection,
  assetsCollection,
  isSearchingSignals,
  isSearchingRuns,
  isSearchingAssets,
  currentRunValue,
  linkedTicketUrl,
  canEditLinkedTicket,
  hasLinkedTicket,
  sendSelectionToChatLabel,
  handleFieldChange,
  handleStatusFieldChange,
  handleAssetsMultiChange,
  handleSignalSearch,
  handleRunSearch,
  handleAssetSearch,
  handleRunChange,
  handleSendFieldContextToChat,
  handleOpenLinkedTicketDialog,
  getMarkableFieldProps,
}: FindingEditGeneralDetailsSectionProps) {
  const t = useTranslations('designSystem.findings.editDrawer');
  const tFindingDetail = useTranslations('designSystem.findings.findingDrawer');
  const selectedAssetIds = editableFields.assets
    .map((asset) => asset.id)
    .filter((id): id is string => id.trim().length > 0);

  return (
    <ObjectEditSection title={t('sections.basicInformation.title')}>
      <ObjectEditFieldGroup>
        <ObjectEditField span="full">
          <Field.Root>
            <Field.Label fontSize="xs" fontWeight="medium">
              {t('sections.basicInformation.fields.title.label')}
            </Field.Label>
            <ObjectContextMarkableField
              field="title"
              fieldLabel={t('sections.basicInformation.fields.title.label')}
              fullText={editableFields.title}
              sendToChatLabel={sendSelectionToChatLabel}
              {...getMarkableFieldProps('title')}
              onSendSelection={handleSendFieldContextToChat}
            >
              <Input
                size="sm"
                value={editableFields.title}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  handleFieldChange('title', event.target.value)
                }
                placeholder={t(
                  'sections.basicInformation.fields.title.placeholder',
                )}
              />
            </ObjectContextMarkableField>
          </Field.Root>
        </ObjectEditField>

        <ObjectEditField span="full">
          <Field.Root>
            <Field.Label fontSize="xs" fontWeight="medium">
              {t('sections.basicInformation.fields.shortDescription.label')}
            </Field.Label>
            <ObjectContextMarkableField
              field="shortDescription"
              fieldLabel={t(
                'sections.basicInformation.fields.shortDescription.label',
              )}
              fullText={editableFields.shortDescription ?? ''}
              sendToChatLabel={sendSelectionToChatLabel}
              {...getMarkableFieldProps('shortDescription')}
              onSendSelection={handleSendFieldContextToChat}
            >
              <Textarea
                size="sm"
                value={editableFields.shortDescription ?? ''}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                  handleFieldChange('shortDescription', event.target.value)
                }
                placeholder={t(
                  'sections.basicInformation.fields.shortDescription.placeholder',
                )}
                rows={3}
                resize="vertical"
              />
            </ObjectContextMarkableField>
          </Field.Root>
        </ObjectEditField>

        <ObjectEditField span="full">
          <Field.Root>
            <Field.Label fontSize="xs" fontWeight="medium">
              {t('sections.basicInformation.fields.description.label')}
            </Field.Label>
            <ObjectContextMarkableField
              field="description"
              fieldLabel={t(
                'sections.basicInformation.fields.description.label',
              )}
              fullText={editableFields.description ?? ''}
              sendToChatLabel={sendSelectionToChatLabel}
              {...getMarkableFieldProps('description')}
              onSendSelection={handleSendFieldContextToChat}
            >
              <Textarea
                size="sm"
                value={editableFields.description ?? ''}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                  handleFieldChange('description', event.target.value)
                }
                placeholder={t(
                  'sections.basicInformation.fields.description.placeholder',
                )}
                rows={4}
                resize="vertical"
              />
            </ObjectContextMarkableField>
          </Field.Root>
        </ObjectEditField>

        <ObjectEditField>
          <Field.Root>
            <Field.Label fontSize="xs" fontWeight="medium">
              {t('sections.basicInformation.fields.status.label')}
            </Field.Label>
            <ObjectContextMarkableField
              field="status"
              fieldLabel={t('sections.basicInformation.fields.status.label')}
              fullText={editableFields.status}
              sendToChatLabel={sendSelectionToChatLabel}
              {...getMarkableFieldProps('status')}
              onSendSelection={handleSendFieldContextToChat}
            >
              <Select.SelectWithOptions
                options={statusOptions.items}
                value={[editableFields.status]}
                onValueChange={(status) =>
                  handleStatusFieldChange(status as FindingStatusType)
                }
                placeholder={t(
                  'sections.basicInformation.fields.status.placeholder',
                )}
                borderRadius="md"
                spaceBetween
              />
            </ObjectContextMarkableField>
          </Field.Root>
        </ObjectEditField>

        <ObjectEditField>
          <Field.Root>
            <Field.Label fontSize="xs" fontWeight="medium">
              {t('sections.basicInformation.fields.category.label')}
            </Field.Label>
            <ObjectContextMarkableField
              field="category"
              fieldLabel={t('sections.basicInformation.fields.category.label')}
              fullText={editableFields.category ?? ''}
              sendToChatLabel={sendSelectionToChatLabel}
              {...getMarkableFieldProps('category')}
              onSendSelection={handleSendFieldContextToChat}
            >
              <Select.Root
                positioning={{ sameWidth: true, strategy: 'fixed' }}
                size="sm"
                collection={categoryOptions}
                value={
                  editableFields.category ? [editableFields.category] : ['']
                }
                onValueChange={(details: { value: string[] }) =>
                  handleFieldChange(
                    'category',
                    details.value[0] === ''
                      ? null
                      : (details.value[0] as SignalCategoryType),
                  )
                }
              >
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText
                      placeholder={t(
                        'sections.basicInformation.fields.category.placeholder',
                      )}
                    />
                  </Select.Trigger>
                </Select.Control>
                <Select.Positioner>
                  <Select.Content maxHeight="300px" overflowY="auto">
                    <Select.ItemGroup>
                      {categoryOptions.items.map((item) => (
                        <Select.Item key={item.value} item={item}>
                          <Select.ItemText>{item.label}</Select.ItemText>
                          <Select.ItemIndicator>
                            <Tick01Icon size={14} />
                          </Select.ItemIndicator>
                        </Select.Item>
                      ))}
                    </Select.ItemGroup>
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>
            </ObjectContextMarkableField>
          </Field.Root>
        </ObjectEditField>

        <ObjectEditField>
          <Field.Root>
            <Field.Label fontSize="xs" fontWeight="medium">
              {t('sections.assignment.fields.signalId.label')}
            </Field.Label>
            <FindingEditSignalCombobox
              collection={signalsCollection}
              selectedValue={editableFields.signalId}
              isSearching={isSearchingSignals}
              onValueChange={(signalId) => {
                handleFieldChange('signalId', signalId);
              }}
              onInputValueChange={handleSignalSearch}
            />
          </Field.Root>
        </ObjectEditField>

        <ObjectEditField>
          <Field.Root>
            <Field.Label fontSize="xs" fontWeight="medium">
              {t('sections.assignment.fields.linkedRun.label')}
            </Field.Label>
            <FindingEditRunCombobox
              collection={runsCollection}
              selectedValue={currentRunValue}
              isSearching={isSearchingRuns}
              onValueChange={handleRunChange}
              onInputValueChange={handleRunSearch}
            />
          </Field.Root>
        </ObjectEditField>

        <ObjectEditField>
          <Field.Root>
            <Field.Label fontSize="xs" fontWeight="medium">
              {t('sections.assignment.fields.assets.label')}
            </Field.Label>
            <FindingEditAssetsMultiSelect
              selectedIds={selectedAssetIds}
              collection={assetsCollection}
              isSearching={isSearchingAssets}
              onValueChange={handleAssetsMultiChange}
              onInputValueChange={handleAssetSearch}
            />
          </Field.Root>
        </ObjectEditField>

        <ObjectEditField span="full">
          <VStack gap={4} alignItems="stretch">
            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="medium">
                {t(
                  'sections.basicInformation.fields.findingUpdateComment.label',
                )}
              </Field.Label>
              <ObjectContextMarkableField
                field="findingUpdateComment"
                fieldLabel={t(
                  'sections.basicInformation.fields.findingUpdateComment.label',
                )}
                fullText={editableFields.findingUpdateComment ?? ''}
                sendToChatLabel={sendSelectionToChatLabel}
                {...getMarkableFieldProps('findingUpdateComment')}
                onSendSelection={handleSendFieldContextToChat}
              >
                <Textarea
                  value={editableFields.findingUpdateComment ?? ''}
                  onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                    handleFieldChange(
                      'findingUpdateComment',
                      event.target.value === '' ? null : event.target.value,
                    )
                  }
                  placeholder={t(
                    'sections.basicInformation.fields.findingUpdateComment.placeholder',
                  )}
                  rows={3}
                  resize="vertical"
                />
              </ObjectContextMarkableField>
            </Field.Root>

            {canEditLinkedTicket && hasLinkedTicket ? (
              <Field.Root>
                <Field.Label fontSize="xs" fontWeight="medium">
                  {tFindingDetail('tabs.overview.linkedTicket')}
                </Field.Label>
                <FindingLinkedTicketActions
                  linkedTicketUrl={linkedTicketUrl}
                  showEditLinkedTicketAction
                  editLinkedTicketLabel={tFindingDetail(
                    'tabs.overview.editLinkedTicket',
                  )}
                  onEditLinkedTicket={handleOpenLinkedTicketDialog}
                />
              </Field.Root>
            ) : null}
          </VStack>
        </ObjectEditField>
      </ObjectEditFieldGroup>
    </ObjectEditSection>
  );
}
